import type { KyInstance } from "ky";
import { parseEventStream } from "./eventStream";

/** One welcome-screen card, as carried by the agent's `capabilities` SSE event. */
export interface CapabilityCard {
  /** Unique card id — the sub-agent name, suffixed (`messaging:0`) when one agent exposes several cards. */
  id: string;
  /** Sub-agent name from the tiger_agent registry (e.g. `scheduling`). */
  agent?: string;
  label: string;
  description: string;
  /** Prompt to send when the card is clicked; null when the manifest offers none. */
  prompt?: string | null;
  examples?: string[];
}

/** `data` payload of the `capabilities` event. */
interface CapabilitiesData {
  agent?: string;
  version?: string;
  cards?: CapabilityCard[];
}

/**
 * Bootstrap the welcome screen from the agent itself: a `{action: "capabilities"}`
 * invocation that the agent answers straight from its sub-agent registry — one
 * `capabilities` SSE event, then `done`. The server short-circuits before its graph,
 * spend gate, and session store, so this call mints no session and burns no budget.
 *
 * ky's default 10s timeout is kept deliberately: unlike a chat turn this stream is
 * tiny, and the caller falls back to static cards on failure, so hanging a cold
 * runtime start is worse than failing fast.
 */
export async function fetchCapabilities(
  api: KyInstance,
  opts: { signal?: AbortSignal } = {},
): Promise<CapabilityCard[]> {
  const response = await api.post("invocations", {
    signal: opts.signal,
    retry: 0,
    json: { action: "capabilities", stream: true },
  });

  for await (const { event, data } of parseEventStream<CapabilitiesData>(
    response,
    "Capabilities request",
  )) {
    if (event === "capabilities" && Array.isArray(data?.cards)) return data.cards;
  }

  throw new Error("Agent stream ended without a capabilities event");
}
